import React, { useEffect, useState, useRef, useCallback } from "react";
import { IconButton, Tooltip, Box, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import {
  ExcelExportPayload,
  GroupDataModel,
  GroupDataModelInfo,
} from "../../types/GroupsModel";
import {
  getExcelData,
  getGroupsData,
  groupIDExists,
  groupStatusChange,
} from "../../api/groupsApi/groupsApi";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { Col, Row } from "react-bootstrap";
import "./Groups.css";
import "./../../styles/userManagmentCommon.css";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../Common/SnackbarContext";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { appSettings } from "../../config/appSetting";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";
interface GroupsTableProps {
  businessUnitData: any;
}

const GroupsTable: React.FC<GroupsTableProps> = ({ businessUnitData }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showEmptySearchMessage, setShowEmptySearchMessage] = useState(false);
  const [rowData, setRowData] = useState<GroupDataModelInfo[]>();
  const gridRef = useRef<AgGridReact>(null);
  const navigation = useNavigate();
  const { showSnackbar } = useSnackbar();

  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
  };

  const Actions = (props: any) => {
    if (props.data === undefined) {
      return "";
    }
    const selectedRowData = props.data;
    const handleEditAction = () => {
      navigation("/groupEditAction", {
        state: { rowData: selectedRowData, action: "Edit" },
      });
    };

    const handleDuplicateAction = () => {
      navigation("/groupEditAction", {
        state: { rowData: selectedRowData, action: "Duplicate" },
      });
    };
    const handleDeactiveStatusClick = async () => {
      const payload = {
        GroupID: selectedRowData?.id,
        contextName: LocalStorageUtil.getItem("contextName"),
      };
      try {
        await groupIDExists(payload);
        handleStatusChange();
        getData(groupDataModel);
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };

    const handleStatusChange = async () => {
      const payload = {
        ContextName: LocalStorageUtil.getItem("contextName"),
        GroupDataModelObj: {
          GroupId: selectedRowData?.id,
        },
      };
      try {
        await groupStatusChange(payload);
        getData(groupDataModel);
      } catch (error) {}
    };

    return (
      <>
        {isPagePermission("Allow Edit","Group",554) && (
          <Tooltip title="Click to edit">
            <EditIcon
              className="icon-button-other"
              onClick={handleEditAction}
              style={{ padding: "2px" }}
            />
          </Tooltip>
        )}
        {isPagePermission("Allow Duplicate","Group",555) && (
          <Tooltip title="Click to duplicate">
            <CopyAllIcon
              className="icon-button-other"
              onClick={handleDuplicateAction}
              style={{ padding: "2px" }}
            />
          </Tooltip>
        )}
        {props.data.active ? (
          <Tooltip title="Click to deactivate the group">
            <PlayCircleIcon
              className="icon-button-other"
              style={{ padding: "2px" }}
              onClick={handleDeactiveStatusClick}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to activate the group">
            <DoDisturbIcon
              className="icon-button-other"
              onClick={handleStatusChange}
              style={{ padding: "0.2vh" }}
            />
          </Tooltip>
        )}
      </>
    );
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "rowIndex",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    {
      headerName: "Group Name",
      field: "groupName",
      tooltipField: "groupName",
      width: 450,
      colId: "groupName",
      flex: 1,
    },
    {
      headerName: "Customer",
      field: "customer",
      tooltipField: "customer",
      width: 450,
      colId: "customer",
      flex: 1,
    },
    {
      headerName: "Roles",
      field: "roles",
      tooltipField: "roles",
      width: 590,
      colId: "roles",
      flex: 1,
    },
    {
      headerName: "Actions",
      field: "link",
      cellRenderer: Actions,
      lockPinned: true,
      suppressSizeToFit: true,
      width: 120,
      minWidth: 120,
      pinned: "right",
    },
  ]);

  const handleCloseSearch = async () => {
    setSearchTerm("");
    setShowEmptySearchMessage(true);
    await getData(groupDataModel);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      setShowEmptySearchMessage(false);
    }
  };

  const handleSearchClick = async () => {
    if (!searchTerm) {
      setShowEmptySearchMessage(true);
      return;
    }
    let updatedGroupDataModel: GroupDataModel = {
      ...groupDataModel,
      SearchText: searchTerm,
    };
    await getData(updatedGroupDataModel);
    setShowEmptySearchMessage(false);
  };

  const getData = async (updatedGroupDataModel: GroupDataModel) => {
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
      GroupDataModelObj: updatedGroupDataModel,
    };
    try {
      const groupsData = await getGroupsData(payload);
      const transformedData = groupsData.groupDataModel.map((group: any) => ({
        id: group.groupId,
        groupName: group.groupName,
        customer: group.accountName,
        roles: group.role.map((role: any) => role.roleName).join(", "),
        active: group.active,
        createdDate: group.createdDate,
      }));
      setRowData(transformedData);
      if (groupsData && searchTerm) {
        showSnackbar(groupsData.totalCount + " records found.");
      }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  let groupDataModel: GroupDataModel = {
    sortOrder: 0,
    currentPage: 1,
    noOfRecords: 25,
    SearchText: "",
    IsSuperAdmin: 1,
    AccountId: Number(LocalStorageUtil.getItem("accountId")),
    isexporttpExcel: false,
    pagename: "Groups",
  };

  useEffect(() => {
    getData(groupDataModel);
  }, []);

  const params: ExcelExportPayload = {
    AccountId: businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: "",
    aliasname: "Groups",
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    isexporttpExcel: true,
    offset: appSettings.timezone,
    pagename: "Groups",
    sortOrder: 0,
  };

  const excelData = async () => {
    try {
      const excelDataResponse = await getExcelData(params);
      showSnackbar("File downloaded successfully!");
      if (!excelDataResponse) {
        showSnackbar("No records Found!");
      }
      const blob = new Blob([excelDataResponse], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "GroupsData.xlsx");
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching Excel data", error);
    }
  };

  const handleCreate = () => {
    navigation("/groupEditAction");
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  //Quick Filter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);

  return (
    <div>
      <NavigationBar
        title="User Management"
        screenTitle="Groups"
        text="Group"
        menuTitle="Configuration"
      />
      <Row style={{ margin: "0.625em" }}>
        <Col>
          <Box>
            <div className="searchContainer">
              <div className="searchWrapper">
                <div style={{ display: "none", alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Search By Group Name"
                    data-testid="search-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyPress}
                    className="searchInput"
                  />
                  <button onClick={handleSearchClick} className="searchButton" data-testid="search-button">
                    <SearchIcon style={{ color: "#006ab6" }} />
                  </button>
                  {searchTerm && (
                    <IconButton
                      onClick={handleCloseSearch}
                      style={{ padding: "0.3125em" }}
                      data-testid="clear-search"
                    >
                      <CloseIcon
                        style={{ color: "var(--aggrid-text-color)" }}
                      />
                    </IconButton>
                  )}
                </div>
                {showEmptySearchMessage && (
                  <span className="errorMessage">
                    {" "}
                    Please enter text for search
                  </span>
                )}
                <input
                  type="text"
                  id="filter-text-box"
                  placeholder="Quick Filter..."
                  style={{
                    width: "25em",
                    height: "2.5em",
                    fontSize: "11px",
                    paddingLeft: ".5em",
                    marginBottom: ".1em",
                  }}
                  onInput={onFilterTextBoxChanged}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "end" }}>
                {isPagePermission("Allow Create New","Group",552) && (
                  <Button
                    variant="contained"
                    size="small"
                    className="button create-new-button"
                    onClick={handleCreate}
                  >
                    Create New
                  </Button>
                )}
                {isPagePermission("Allow Export To Excel","Group",553) && (
                  <button
                    onClick={excelData}
                    className="excel-button"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <img
                      src={ExcelIcon}
                      alt="Download Excel"
                      className="excel-icon"
                      style={{ marginTop: ".3em" }}
                    />
                  </button>
                )}
              </div>
            </div>
            <div className="rp-main-inner col-sm-12">
              <div className="rp-main-inner col-sm-12">
                <div
                  className="ag-theme-quartz-dark"
                  style={{
                    height: "66vh",
                    marginTop: ".1vh",
                    width: "100%",
                  }}
                >
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rowData}
                    pagination={true}
                    headerHeight={50}
                    paginationPageSize={defaulPageSize}
                    paginationPageSizeSelector={pageSizeSelector}
                    rowGroupPanelShow="always"
                    pivotPanelShow="always"
                    gridOptions={{
                      rowSelection: { 
                        mode : "multiRow",
                        checkboxes: true,
                        headerCheckbox: true,
                        enableClickSelection: false,
                       },  
                    }}
                    defaultColDef={defaultColDef}
                    ref={gridRef}
                    overlayNoRowsTemplate={
                      '<span class="ag-overlay-loading-center">No rows to show</span>'
                    }
                    loading={!rowData}
                    loadingOverlayComponent={CustomLoadingOverlay}
                  />
                </div>
              </div>
            </div>
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default GroupsTable;
